// Textbausteine (Snippets) für das "/"-Menü der Editoren (Runde 8, Agent 8D; K-32). Der Hook lädt die eigenen und geteilten Bausteine
// (`textbausteineApi.liste`, einmal je Sitzung und höchstens alle zwei Minuten neu) und liefert sie in der Form des Editors
// (`Baustein` aus `kommunikation/Editor.tsx`: `{id, name, html}`). Das Kürzel steht vorn im Namen, damit das "/"-Menü danach filtert:
// "/gruss" findet "gruss · Begrüßung". Die Liste ist stabil (gleiche Verweise, solange sich nichts ändert), damit `useMemo`
// der Aufrufer nicht neu rechnet. Eingebaut ist er im E-Mail-Komposer, in der Notiz (7B) und in der SMS-Karte (`SlashMenue.tsx`).
//
// Nutzung zählen: der Editor kennt nur `{id, name, html}`. Der Text eines Bausteins wird erst gelesen, wenn jemand ihn wählt;
// deshalb ist `html` hier ein Lese-Zugriff, der beim Wählen `POST /api/textbausteine/{id}/genutzt` auslöst (höchstens einmal in
// drei Sekunden je Baustein). Der Weg ist ein Notbehelf, bis `Baustein` ein Feld `onWahl` bekommt (Wunsch in AENDERUNGSWUENSCHE.md).
import { useEffect, useState } from "react";
import { textbausteineApi, type BausteinKanal, type Textbaustein } from "../api/textbausteine";
import { beimNutzerwechsel } from "../daten/sitzung";
import type { Baustein } from "../kommunikation/Editor";
import { textZuHtml } from "./htmlText";

const FRISCH_MS = 2 * 60 * 1000;

let alle: Textbaustein[] | null = null;
let geladenAm = 0;
let laeuft: Promise<void> | null = null;
let version = 0;
const hoerer = new Set<() => void>();
const abgeleitet = new Map<BausteinKanal, { version: number; liste: Baustein[] }>();
const zuletztGezaehlt = new Map<string, number>();

beimNutzerwechsel(() => {
  alle = null;
  geladenAm = 0;
  version += 1;
  abgeleitet.clear();
  hoerer.forEach((fn) => fn());
});

function melden() {
  version += 1;
  hoerer.forEach((fn) => fn());
}

/** Liste neu holen (nach dem Anlegen, Ändern oder Löschen in den Einstellungen). */
export async function textbausteineNeuLaden(): Promise<void> {
  if (laeuft) return laeuft;
  laeuft = textbausteineApi
    .liste()
    .then((r) => {
      alle = r.data;
      geladenAm = Date.now();
      melden();
    })
    .catch(() => {
      /* ohne Liste bleibt das Menü ohne Bausteine; die Platzhalter und Formate funktionieren weiter */
    })
    .finally(() => {
      laeuft = null;
    });
  return laeuft;
}

function frischHalten() {
  if (!alle || Date.now() - geladenAm > FRISCH_MS) void textbausteineNeuLaden();
}

/** Eine Nutzung melden (sortiert das Menü nach Häufigkeit). Fehler bleiben stumm. */
export function textbausteinGenutzt(id: string): void {
  const jetzt = Date.now();
  if (jetzt - (zuletztGezaehlt.get(id) ?? 0) < 3000) return;
  zuletztGezaehlt.set(id, jetzt);
  void textbausteineApi.genutzt(id).catch(() => undefined);
  if (alle) alle = alle.map((b) => (b.id === id ? { ...b, nutzungen: b.nutzungen + 1 } : b));
}

/** Menüeintrag ("kuerzel · Name") für einen Baustein. */
export function menueName(b: Pick<Textbaustein, "kuerzel" | "name">): string {
  return b.name && b.name !== b.kuerzel ? `${b.kuerzel} · ${b.name}` : b.kuerzel;
}

function alsBaustein(b: Textbaustein): Baustein {
  const html = b.kanal === "sms" ? textZuHtml(b.inhalt) : b.inhalt;
  const eintrag = { id: b.id, name: menueName(b) } as Baustein;
  Object.defineProperty(eintrag, "html", {
    enumerable: true,
    get() {
      textbausteinGenutzt(b.id);
      return html;
    },
  });
  return eintrag;
}

function fuerKanal(kanal: BausteinKanal): Baustein[] {
  const vorhanden = abgeleitet.get(kanal);
  if (vorhanden && vorhanden.version === version) return vorhanden.liste;
  const liste = (alle ?? [])
    .filter((b) => b.kanal === "alle" || b.kanal === kanal || kanal === "alle")
    .map(alsBaustein);
  abgeleitet.set(kanal, { version, liste });
  return liste;
}

/** Textbausteine des Kanals für das "/"-Menü; leer, bis die Liste da ist. */
export function useTextbausteine(kanal: BausteinKanal = "email"): Baustein[] {
  const [, setZaehler] = useState(0);
  useEffect(() => {
    const fn = () => setZaehler((z) => z + 1);
    hoerer.add(fn);
    frischHalten();
    return () => {
      hoerer.delete(fn);
    };
  }, []);
  return fuerKanal(kanal);
}
